import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, Eye, Info, Sparkles } from 'lucide-react';
import { PortfolioItem } from '@/hooks/profile';
import { generatePortfolioDetailsWithFallback, generatePortfolioTitle, generatePortfolioDescription, getAnalysisStatusMessageForUI, getCostOptimizationMessageForUI } from '@/utils/portfolioGenerator';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RichTextEditor } from '@/components/ui/rich-text-editor';

interface ProjectDetailsFieldsProps {
  item: PortfolioItem;
  onChange: (item: PortfolioItem) => void;
  imageUrls: string[];
  userProfession?: string;
  userSkills?: string[];
  isAutoGenerating?: boolean;
  detectedElements?: string[];
}

const ProjectDetailsFields: React.FC<ProjectDetailsFieldsProps> = ({
  item,
  onChange,
  imageUrls,
  userProfession = 'designer',
  userSkills = [],
  isAutoGenerating = false,
  detectedElements = []
}) => {
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [showAnalysisInfo, setShowAnalysisInfo] = useState(false);
  const [lastAnalysisResult, setLastAnalysisResult] = useState<any>(null);
  const { toast } = useToast();

  const handleGenerateTitle = async () => {
    if (imageUrls.length === 0) return;
    
    setIsGeneratingTitle(true);
    try {
      const title = await generatePortfolioTitle({
        imageUrls,
        userProfession,
        userSkills: [] // Don't pass user skills to focus on image analysis
      });
      
      onChange({ ...item, title });
      
      toast({
        title: "Title generated",
        description: "We have analyzed your images and generated a title based on the visual content.",
      });
    } catch (error) {
      console.error('Failed to generate title:', error);
      toast({
        title: "Error generating title",
        description: "There was a problem generating your title. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  const handleGenerateDescription = async () => {
    if (imageUrls.length === 0) return;
    
    setIsGeneratingDescription(true);
    try {
      const description = await generatePortfolioDescription({
        imageUrls,
        userProfession,
        userSkills: [] // Don't pass user skills to focus on image analysis
      });
      // Description is already wrapped in HTML from the generator
      onChange({ ...item, description });
      toast({
        title: "Description generated",
        description: "We have analyzed your images and generated a description based on the visual content.",
      });
    } catch (error) {
      console.error('Failed to generate description:', error);
      toast({
        title: "Error generating description",
        description: "There was a problem generating your description. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleGenerateBoth = async () => {
    if (imageUrls.length === 0) {
      toast({
        title: "No images available",
        description: "Please upload at least one image to generate both title and description.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingTitle(true);
    setIsGeneratingDescription(true);
    try {
      const result = await generatePortfolioDetailsWithFallback({
        imageUrls,
        userProfession,
        userSkills: [] // Don't pass user skills to focus on image analysis
      });
      // Wrap description in <p> for rich text
      onChange({ ...item, title: result.title, description: `<p>${result.description}</p>` });
      setLastAnalysisResult(result);
      toast({
        title: "Title and description generated",
        description: "We have analyzed your images and generated content based on the visual elements.",
      });
    } catch (error) {
      console.error('Error generating title and description:', error);
      toast({
        title: "Error generating title and description",
        description: "There was a problem generating your title and description. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingTitle(false);
      setIsGeneratingDescription(false);
    }
  };

  const hasImages = imageUrls.length > 0;
  const isAnyGenerating = isAutoGenerating || isGeneratingTitle || isGeneratingDescription;
  const totalImages = imageUrls.length;
  const analyzedImages = Math.min(totalImages, 3);

  return (
    <div className="space-y-6">
      {/* Analysis Status */}
      {isAnyGenerating && (
        <Alert>
          <AlertDescription className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {getAnalysisStatusMessageForUI(analyzedImages, totalImages, true)}
          </AlertDescription>
        </Alert>
      )}

      {/* Title Field */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="title">Project Title</Label>
          {/* <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerateTitle}
              disabled={isAnyGenerating || !hasImages}
              className="text-xs"
            >
              {isGeneratingTitle ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-3 w-3 mr-1" />
                  Generate
                </>
              )}
            </Button>
          </div> */}
        </div>
        <Input
          id="title"
          value={item.title}
          onChange={(e) => onChange({ ...item, title: e.target.value })}
          placeholder={isAutoGenerating ? "We are analyzing images..." : "e.g., E-commerce Website Redesign"}
          disabled={isAutoGenerating}
          maxLength={100}
        />
        <div className="text-xs text-muted-foreground">
          {item.title.length}/100 characters
        </div>
      </div>

      {/* Description Field */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="description">Project Description</Label>
        </div>
        <RichTextEditor
          value={item.description}
          onChange={(value) => onChange({ ...item, description: value })}
          placeholder={isAutoGenerating ? "We are analyzing images..." : "Describe your project in detail..."}
          disabled={isAutoGenerating}
          maxLength={2000}
        />
      </div>
    </div>
  );
};

export default ProjectDetailsFields;
